import {announce} from '@github-ui/aria-live'
import {ChevronDownIcon, PlusIcon, ServerIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, Banner, Button, Dialog, Spinner} from '@primer/react'
import {UnderlinePanels} from '@primer/react/experimental'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {isConnected, filterPlugins, pluginDisplayName, type Plugin} from '../types/connectors'
import {filterCustomizations, toMcpCustomizations, type McpCustomization} from '../types/customization'
import type {McpServerConfig, McpServerMap} from '../types/mcp'
import type {ConnectorActionSource} from '../utils/connector-analytics'
import {sendMcpUiEvent} from '../utils/mcp-analytics'
import {ConnectorDetailDialog} from './ConnectorDetailDialog'
import {ConnectorSection} from './ConnectorSection'
import {CustomizeEmptyState} from './CustomizeEmptyState'
import {CustomizeSearch} from './CustomizeSearch'
import {McpServerSection} from './McpServerSection'
import {McpServerDialog} from './McpServerDialog'
import styles from './CustomizePage.module.css'

type TabId = 'browse' | 'installed'

export interface CustomizePageProps {
  /** Full connector catalog, each entry annotated with the caller's connection status. */
  plugins: Plugin[]
  onConnect?: (plugin: Plugin, source: ConnectorActionSource) => void
  onDisconnect?: (plugin: Plugin, source: ConnectorActionSource) => void
  /** Names of the connectors with a request in flight. */
  busyConnectors?: ReadonlySet<string>
  /** Tab shown on first render. */
  defaultTab?: TabId
  /** Caveat shown below the details table in the dialog. */
  dialogNote?: string
  /** Shows a spinner in place of the connector rows while the catalog is first read. */
  loading?: boolean
  /** Shown in place of the connector rows when the catalog could not be read. */
  loadError?: string | null
  /** Shown in the detail dialog, or above the tabs once the dialog is closed. */
  actionError?: string | null
  /** Retries the catalog read. */
  onRetry?: () => void
  /** The user's own MCP servers. Absent when the feature is not enabled. */
  mcpServers?: McpServerMap
  /** Labels an in-memory preview that does not persist or connect servers. */
  mcpPreview?: boolean
  /** Shows a spinner in place of the MCP rows while the servers are first read. */
  mcpLoading?: boolean
  /** Shown in place of the MCP rows when the servers could not be read. */
  mcpLoadError?: string | null
  /**
   * Changes whenever `mcpLoadError` reflects a newly settled failure,
   * including a retry that fails with the same message, so it can be
   * announced once per attempt rather than once per distinct message.
   */
  mcpLoadErrorAt?: number | null
  /** Retries the MCP server read. */
  onRetryMcp?: () => void
  /** Opens the add/edit dialog. Without it the Add button is not rendered. */
  onSaveMcpServer?: (name: string, config: McpServerConfig, editingName?: string) => Promise<void> | void
  onDeleteMcpServer?: (name: string) => Promise<void> | void
}

const NO_BUSY_CONNECTORS: ReadonlySet<string> = new Set()

const INSTALLED_DESCRIPTION = 'Connected services available in Copilot App and the Copilot CLI.'
const AVAILABLE_DESCRIPTION = 'Browse connectors you can add to Copilot.'
const MCP_DESCRIPTION = 'MCP servers saved in your personal Copilot settings.'

const ANNOUNCE_DELAY_MS = 500

/**
 * Loading and failure belong to a whole data source, so they stand in for the
 * section's own empty copy rather than replacing the page.
 *
 * Each source resolves separately. A failed connector read leaves the MCP
 * section, the tabs, and the Add button intact, and the same holds in reverse.
 */
function renderSourceState({
  loading,
  error,
  onRetry,
  loadingLabel,
  errorHeading,
}: {
  loading: boolean
  error: string | null
  onRetry?: () => void
  loadingLabel: string
  errorHeading: string
}): React.ReactNode {
  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner srText={loadingLabel} />
      </div>
    )
  }
  if (error) {
    return <CustomizeEmptyState heading={errorHeading} description={error} onRetry={onRetry} />
  }
  return null
}

export function CustomizePage({
  plugins,
  onConnect,
  onDisconnect,
  busyConnectors = NO_BUSY_CONNECTORS,
  defaultTab = 'browse',
  dialogNote,
  loading = false,
  loadError = null,
  actionError = null,
  onRetry,
  mcpServers,
  mcpPreview = false,
  mcpLoading = false,
  mcpLoadError = null,
  mcpLoadErrorAt = null,
  onRetryMcp,
  onSaveMcpServer,
  onDeleteMcpServer,
}: CustomizePageProps) {
  const [selectedName, setSelectedName] = useState<string | null>(null)
  const [query, setQuery] = useState('')
  const [tab, setTab] = useState<TabId>(defaultTab)
  /** `null` means the dialog is closed. A name means edit, `''` means add. */
  const [editingMcpName, setEditingMcpName] = useState<string | null>(null)
  const [removingMcpName, setRemovingMcpName] = useState<string | null>(null)
  const [mcpActionError, setMcpActionError] = useState<string | null>(null)
  const [mcpBusy, setMcpBusy] = useState(false)
  const [mcpAnnouncement, setMcpAnnouncement] = useState('')
  const [addMenuOpen, setAddMenuOpen] = useState(false)
  const mcpDialogRef = useRef<HTMLDivElement>(null)
  const mcpInFlightRef = useRef(false)
  const addMcpRef = useRef<HTMLButtonElement>(null)
  const mcpReturnFocusRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- announce after the modal unmounts so the live region is reachable
    if (mcpAnnouncement) announce(mcpAnnouncement)
  }, [mcpAnnouncement])

  const openMcpEditor = (name: string, trigger: HTMLButtonElement | null = addMcpRef.current) => {
    if (!mcpPreview) {
      if (name === '') {
        sendMcpUiEvent('add_server_click', {source: 'add_menu', action: 'create'})
      } else {
        sendMcpUiEvent('edit_click', {source: 'row', action: 'edit', transport: mcpServers?.[name]?.type})
      }
    }
    mcpReturnFocusRef.current = trigger
    setMcpActionError(null)
    setEditingMcpName(name)
  }

  const saveMcpServer = async (name: string, config: McpServerConfig) => {
    if (mcpInFlightRef.current || !onSaveMcpServer) return
    mcpInFlightRef.current = true
    setMcpBusy(true)
    setMcpAnnouncement('')
    setMcpActionError(null)
    try {
      await onSaveMcpServer(name, config, editingMcpName || undefined)
      if (name !== editingMcpName || !mcpReturnFocusRef.current?.isConnected) {
        mcpReturnFocusRef.current = addMcpRef.current
      }
      setTab('installed')
      setQuery('')
      setEditingMcpName(null)
      setMcpAnnouncement(
        mcpPreview ? `${name} updated in this preview only. Shown in Installed.` : `${name} saved. Shown in Installed.`,
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not save this MCP server.'
      setMcpActionError(message)
      announce(message, {element: mcpDialogRef.current ?? undefined})
    } finally {
      mcpInFlightRef.current = false
      setMcpBusy(false)
    }
  }

  const removeMcpServer = async () => {
    if (mcpInFlightRef.current || removingMcpName === null || !onDeleteMcpServer) return
    mcpInFlightRef.current = true
    setMcpBusy(true)
    setMcpAnnouncement('')
    setMcpActionError(null)
    try {
      await onDeleteMcpServer(removingMcpName)
      mcpReturnFocusRef.current = addMcpRef.current
      setRemovingMcpName(null)
      setMcpAnnouncement(
        mcpPreview ? `${removingMcpName} removed from this preview only.` : `${removingMcpName} removed.`,
      )
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not remove this MCP server.'
      setMcpActionError(message)
      announce(message, {element: mcpDialogRef.current ?? undefined})
    } finally {
      mcpInFlightRef.current = false
      setMcpBusy(false)
    }
  }

  const cancelMcpRemoval = () => {
    if (mcpInFlightRef.current || removingMcpName === null) return
    if (!mcpPreview) {
      sendMcpUiEvent('cancel', {
        source: 'dialog',
        action: 'remove',
        transport: mcpServers?.[removingMcpName]?.type,
      })
    }
    setRemovingMcpName(null)
  }

  const mcpEnabled = onSaveMcpServer !== undefined
  const allMcpServers = useMemo(() => toMcpCustomizations(mcpServers), [mcpServers])

  const installed = useMemo(() => plugins.filter(isConnected), [plugins])

  const visibleBrowse = useMemo(() => filterPlugins(plugins, query), [plugins, query])
  const visibleInstalled = useMemo(() => filterPlugins(installed, query), [installed, query])
  const visibleMcpServers = useMemo(
    () => filterCustomizations(allMcpServers, query) as McpCustomization[],
    [allMcpServers, query],
  )
  const trimmedQuery = query.trim()
  const resultsDescription = `Results for “${trimmedQuery}” across all customizations.`

  // Personal MCP servers live on Installed, so they count toward whether that tab
  // has anything to search and toward the result total it announces.
  const installedTotal = installed.length + allMcpServers.length
  const visibleInstalledTotal = visibleInstalled.length + visibleMcpServers.length

  // A tab is only searchable when it has rows to search, which is also what gates
  // its search box. Without this, Browse reports no matches for a query typed on
  // Installed, with no local way to clear it.
  const browseSearching = trimmedQuery.length > 0 && plugins.length > 0
  const installedSearching = trimmedQuery.length > 0 && installedTotal > 0
  const activeSearching = tab === 'browse' ? browseSearching : installedSearching
  const activeCount = tab === 'browse' ? visibleBrowse.length : visibleInstalledTotal

  // Results change while focus stays in the search box, so only the active tab's
  // outcome is announced.
  useEffect(() => {
    if (!activeSearching) return

    const timer = setTimeout(() => {
      announce(
        activeCount === 0
          ? `No customizations match “${trimmedQuery}”`
          : `${activeCount} ${activeCount === 1 ? 'customization matches' : 'customizations match'} “${trimmedQuery}”`,
      )
    }, ANNOUNCE_DELAY_MS)

    return () => clearTimeout(timer)
  }, [activeSearching, activeCount, trimmedQuery])

  // Read the selection back out of `plugins` so the dialog reflects status changes.
  const selected = selectedName === null ? null : (plugins.find(plugin => plugin.name === selectedName) ?? null)

  // Row-triggered connections need feedback without opening the detail dialog.
  const rowConnectingRef = useRef<string | null>(null)
  const announcedErrorRef = useRef<string | null>(null)
  const announcedMcpErrorAtRef = useRef<number | null>(null)

  const handleRowConnect = useCallback(
    (plugin: Plugin) => {
      rowConnectingRef.current = plugin.name
      onConnect?.(plugin, 'row')
    },
    [onConnect],
  )

  useEffect(() => {
    const name = rowConnectingRef.current
    if (name === null) return
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- consent finishes in another tab, so the connected status arrives by poll rather than by an event this page sees
    const plugin = plugins.find(entry => entry.name === name)
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- same poll-driven status change
    if (!plugin || !isConnected(plugin)) return

    rowConnectingRef.current = null
    announce(`${pluginDisplayName(plugin)} connected`)
  }, [plugins])

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the failure arrives from the parent's request, not from an event this page sees
    if (!actionError) {
      announcedErrorRef.current = null
      return
    }
    rowConnectingRef.current = null
    // The dialog announces its own errors, and the banner it replaces is not a
    // live region.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- same parent-owned request
    if (selectedName !== null || announcedErrorRef.current === actionError) return
    announcedErrorRef.current = actionError
    announce(actionError)
  }, [actionError, selectedName])

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the failure arrives from the parent's settings read, not from an event this page sees
    if (!mcpLoadError || mcpLoadErrorAt === null || tab !== 'installed') return
    // A distinct `mcpLoadErrorAt` per settled attempt, so a retry that fails
    // with the same message is announced again instead of silently skipped.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- same parent-owned read
    if (announcedMcpErrorAtRef.current === mcpLoadErrorAt) return
    announcedMcpErrorAtRef.current = mcpLoadErrorAt
    announce(mcpLoadError)
  }, [mcpLoadError, mcpLoadErrorAt, tab])

  const connectorsState = renderSourceState({
    loading,
    error: loadError,
    onRetry,
    loadingLabel: 'Loading connectors',
    errorHeading: 'Connectors could not be loaded',
  })
  const mcpState = renderSourceState({
    loading: mcpLoading,
    error: mcpLoadError,
    onRetry: onRetryMcp,
    loadingLabel: 'Loading MCP servers',
    errorHeading: 'MCP servers could not be loaded',
  })

  // An unresolved source still owns a section, so its outcome has somewhere to
  // render and the tab never claims to be empty while a read is pending or lost.
  const showInstalledConnectors = installed.length > 0 || connectorsState !== null
  const showInstalledMcp = allMcpServers.length > 0 || mcpState !== null

  const catalog = (
    <div className={styles.tabs}>
      <UnderlinePanels aria-label="Customize" value={tab} onChange={({value}) => setTab(value as TabId)}>
        <UnderlinePanels.Tab value="browse">Browse</UnderlinePanels.Tab>
        <UnderlinePanels.Tab value="installed">Installed</UnderlinePanels.Tab>

        <UnderlinePanels.Panel value="browse" className={styles.panel}>
          {plugins.length > 0 ? (
            <CustomizeSearch value={query} onChange={setQuery} label="Search available customizations" />
          ) : null}
          <ConnectorSection
            title="Available"
            description={browseSearching ? resultsDescription : AVAILABLE_DESCRIPTION}
            plugins={connectorsState ? [] : visibleBrowse}
            onSelect={plugin => setSelectedName(plugin.name)}
            onConnect={handleRowConnect}
            busyConnectors={busyConnectors}
            emptyState={
              connectorsState ??
              (browseSearching ? (
                <CustomizeEmptyState
                  heading="No matching customizations"
                  description={`No available customizations match “${trimmedQuery}”.`}
                />
              ) : (
                <CustomizeEmptyState
                  heading="No customizations available"
                  description="There are no customizations to add right now. Check back later."
                />
              ))
            }
          />
        </UnderlinePanels.Panel>

        <UnderlinePanels.Panel value="installed" className={styles.panel}>
          {installedTotal > 0 ? (
            <CustomizeSearch value={query} onChange={setQuery} label="Search installed customizations" />
          ) : null}
          {showInstalledConnectors || showInstalledMcp ? (
            <>
              {/* Each source keeps its own section, since a GitHub-run connector
                  and a server the user wrote are managed in different places. */}
              {showInstalledConnectors ? (
                <ConnectorSection
                  title="Connectors"
                  description={installedSearching ? resultsDescription : INSTALLED_DESCRIPTION}
                  plugins={connectorsState ? [] : visibleInstalled}
                  onSelect={plugin => setSelectedName(plugin.name)}
                  onConnect={handleRowConnect}
                  busyConnectors={busyConnectors}
                  emptyState={
                    connectorsState ?? (
                      <CustomizeEmptyState
                        heading="No matching connectors"
                        description={`No connectors match “${trimmedQuery}”.`}
                      />
                    )
                  }
                />
              ) : null}
              {showInstalledMcp ? (
                <McpServerSection
                  title="MCP servers"
                  description={
                    installedSearching
                      ? resultsDescription
                      : mcpPreview
                        ? 'MCP servers added to this preview only.'
                        : MCP_DESCRIPTION
                  }
                  servers={mcpState ? [] : visibleMcpServers}
                  onEdit={(server, trigger) => openMcpEditor(server.id, trigger)}
                  onDelete={
                    onDeleteMcpServer
                      ? (server, trigger) => {
                          if (!mcpPreview) {
                            sendMcpUiEvent('remove_click', {
                              source: 'row',
                              action: 'remove',
                              transport: server.transport,
                            })
                          }
                          mcpReturnFocusRef.current = trigger
                          setMcpActionError(null)
                          setRemovingMcpName(server.id)
                        }
                      : undefined
                  }
                  emptyState={
                    mcpState ?? (
                      <CustomizeEmptyState
                        heading="No matching MCP servers"
                        description={`No MCP servers match “${trimmedQuery}”.`}
                      />
                    )
                  }
                />
              ) : null}
            </>
          ) : (
            <CustomizeEmptyState
              headingLevel="h2"
              heading="No customizations installed"
              description="Customizations you add appear here. Browse the catalog to connect one."
            />
          )}
        </UnderlinePanels.Panel>
      </UnderlinePanels>
    </div>
  )

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Customize</h1>
        {mcpEnabled ? (
          <ActionMenu
            anchorRef={addMcpRef}
            open={addMenuOpen}
            onOpenChange={open => {
              const canOpen = open && !mcpLoading && !mcpLoadError
              if (canOpen && !addMenuOpen && !mcpPreview) sendMcpUiEvent('add_click', {source: 'header'})
              setAddMenuOpen(canOpen)
            }}
          >
            <ActionMenu.Button
              variant="primary"
              leadingVisual={PlusIcon}
              trailingAction={ChevronDownIcon}
              inactive={mcpLoading || !!mcpLoadError}
              aria-disabled={mcpLoading || !!mcpLoadError}
            >
              Add
            </ActionMenu.Button>
            <ActionMenu.Overlay align="end">
              <ActionList>
                <ActionList.Item
                  disabled={mcpLoading || !!mcpLoadError}
                  onSelect={() => {
                    if (!mcpLoading && !mcpLoadError) openMcpEditor('')
                  }}
                >
                  <ActionList.LeadingVisual>
                    <ServerIcon />
                  </ActionList.LeadingVisual>
                  MCP server...
                </ActionList.Item>
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        ) : null}
      </div>

      {actionError && !selected ? (
        <Banner variant="critical" title="Connector action failed" hideTitle description={actionError} />
      ) : null}

      {catalog}

      {selected ? (
        <ConnectorDetailDialog
          plugin={selected}
          busy={busyConnectors.has(selected.name)}
          actionError={actionError}
          note={dialogNote}
          onClose={() => setSelectedName(null)}
          onConnect={(plugin, source) => onConnect?.(plugin, source)}
          onDisconnect={plugin => onDisconnect?.(plugin, 'dialog')}
        />
      ) : null}
      {editingMcpName !== null && onSaveMcpServer ? (
        <McpServerDialog
          ref={mcpDialogRef}
          editingName={editingMcpName === '' ? undefined : editingMcpName}
          editingConfig={editingMcpName === '' ? undefined : mcpServers?.[editingMcpName]}
          existingNames={Object.keys(mcpServers ?? {})}
          saveError={mcpActionError}
          busy={mcpBusy}
          telemetryEnabled={!mcpPreview}
          returnFocusRef={mcpReturnFocusRef}
          onClose={() => {
            if (!mcpInFlightRef.current) setEditingMcpName(null)
          }}
          onSave={saveMcpServer}
        />
      ) : null}
      {removingMcpName !== null ? (
        <Dialog
          ref={mcpDialogRef}
          className={styles.mcpDialog}
          title={`Remove ${removingMcpName}?`}
          onClose={cancelMcpRemoval}
          returnFocusRef={mcpReturnFocusRef}
          renderFooter={() => (
            <Dialog.Footer>
              <Button inactive={mcpBusy} aria-disabled={mcpBusy} onClick={cancelMcpRemoval}>
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={mcpBusy}
                loadingAnnouncement="Removing server"
                onClick={removeMcpServer}
              >
                Remove server
              </Button>
            </Dialog.Footer>
          )}
        >
          <p>
            {mcpPreview
              ? 'This removes the server from this preview only. Your personal Copilot settings are unchanged.'
              : 'This removes the server from your personal Copilot settings.'}
          </p>
          {mcpActionError ? (
            <Banner variant="critical" title="Server could not be removed" description={mcpActionError} />
          ) : null}
        </Dialog>
      ) : null}
    </div>
  )
}
